import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Stack,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import { Close, Add, Edit, Delete } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { apiService } from '@/services/api';
import { IconSettings } from '@tabler/icons-react';
import { useTranslation } from '@/hooks/useTranslation';

interface Solution {
  externalId: string;
  categoryId: string;
  category: string;
  vehicleTypeId: string;
  vehicleType: string;
  name: string;
  alias: string;
  level: number;
  isFreeWhenStageSelected: boolean;
  createdAt: string;
  updatedAt: string;
}

interface SolutionSection {
  title: string;
  items: Solution[];
}

interface SolutionGroupPriceItem {
  groupId: string;
  groupName: string;
  creditAmount: number;
}

interface PricingRule {
  externalId: string;
  vehicleTypeId: string;
  vehicleType: {
    externalId: string;
    name: string;
  };
  customerGroupId: string;
  customerGroup: {
    externalId: string;
    name: string;
  };
  alias: string;
  creditAmount: number;
  createdAt: string;
  updatedAt: string;
}

const SolutionsPage: React.FC = () => {
  const { t, getCurrentLanguage } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const theme = useTheme();
  const { user } = useAuth();
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [solutionSections, setSolutionSections] = useState<SolutionSection[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deletingSolution, setDeletingSolution] = useState<Solution | null>(
    null
  );
  const [deleting, setDeleting] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [categories, setCategories] = useState<
    Array<{ externalId: string; name: string }>
  >([]);
  const [vehicleTypes, setVehicleTypes] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [selectedVehicleTypeId, setSelectedVehicleTypeId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState({
    categoryId: '',
    vehicleTypeId: '',
    name: '',
    alias: '',
    level: 0,
    isFreeWhenStageSelected: false,
  });
  const [addFormData, setAddFormData] = useState({
    categoryId: '',
    vehicleTypeId: '',
    name: '',
    alias: '',
    level: 0,
    isFreeWhenStageSelected: false,
  });
  const [groupPrices, setGroupPrices] = useState<SolutionGroupPriceItem[]>([]);
  // const [groupPricesTabIndex, setGroupPricesTabIndex] = useState<number>(0);
  const [isPricingRulesModalOpen, setIsPricingRulesModalOpen] = useState<boolean>(false);
  const [selectedPricingVehicleTypeId, setSelectedPricingVehicleTypeId] = useState<string>('');
  const [selectedPricingCustomerGroupId, setSelectedPricingCustomerGroupId] = useState<string>('');
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  const [loadingPricingRules, setLoadingPricingRules] = useState<boolean>(false);
  const [editingPricingRules, setEditingPricingRules] = useState<Map<string, number>>(new Map());
  const [savingPricingRule, setSavingPricingRule] = useState<string | null>(null);
  const [customerGroups, setCustomerGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [selectedSolutions, setSelectedSolutions] = useState<string[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [calculatingPrice, setCalculatingPrice] = useState<boolean>(false);

  const levelOptions = [
    { value: 1, label: 'Stage' },
    { value: 2, label: 'Free when Stage selected' },
    { value: 3, label: 'Subcategory' },
    { value: 4, label: 'Individual' },
  ];

  const buildCustomerSections = (
    solutionsList: Solution[],
    filterVehicleTypeId?: string
  ): SolutionSection[] => {
    // Filter by vehicle type if specified
    const filteredSolutions = filterVehicleTypeId
      ? solutionsList.filter(solution => solution.vehicleTypeId === filterVehicleTypeId)
      : solutionsList;

    // Group solutions by category
    const solutionsByCategory = new Map<string, Solution[]>();

    filteredSolutions.forEach(solution => {
      const categoryName = solution.category || 'Uncategorized';
      if (!solutionsByCategory.has(categoryName)) {
        solutionsByCategory.set(categoryName, []);
      }
      solutionsByCategory.get(categoryName)!.push(solution);
    });

    // Hardcoded category order for customer view: Tuning, Deactivations, Individual
    const categoryOrder = ['Tuning', 'Free', 'Deactivations', 'Individual'];

    // Helper function to get category index (case-insensitive)
    const getCategoryIndex = (categoryName: string): number => {
      const normalizedName = categoryName.trim();
      const index = categoryOrder.findIndex(
        orderName => orderName.toLowerCase() === normalizedName.toLowerCase()
      );
      return index;
    };

    // Convert to sections array
    const allSections: SolutionSection[] = Array.from(
      solutionsByCategory.entries()
    )
      .map(([categoryName, items]) => {
        // Sort solutions within category by updatedAt (newest first)
        const sortedItems = [...items].sort((a, b) => {
          const dateA = new Date(a.updatedAt).getTime();
          const dateB = new Date(b.updatedAt).getTime();
          return dateB - dateA; // Descending order (newest first)
        });

        return {
          title: categoryName,
          items: sortedItems,
        };
      })
      .filter(section => section.items.length > 0);

    // Sort sections according to hardcoded order
    const sections: SolutionSection[] = allSections.sort((a, b) => {
      const indexA = getCategoryIndex(a.title);
      const indexB = getCategoryIndex(b.title);

      // If both are in the order list, sort by their position
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only A is in the order list, A comes first
      if (indexA !== -1) {
        return -1;
      }
      // If only B is in the order list, B comes first
      if (indexB !== -1) {
        return 1;
      }
      // If neither is in the order list, sort alphabetically
      return a.title.localeCompare(b.title);
    });

    return sections;
  };

  // Load customer groups when pricing rules modal opens
  useEffect(() => {
    if (isPricingRulesModalOpen && customerGroups.length === 0) {
      const loadCustomerGroups = async () => {
        try {
          const response = await apiService.getCustomerGroups();
          setCustomerGroups(response.groups);
        } catch (err: any) {
          console.error('Error loading customer groups:', err);
        }
      };
      loadCustomerGroups();
    }
  }, [isPricingRulesModalOpen, customerGroups.length]);

  // Load pricing rules when both selects are chosen
  useEffect(() => {
    if (
      isPricingRulesModalOpen &&
      selectedPricingVehicleTypeId &&
      selectedPricingCustomerGroupId
    ) {
      const loadPricingRules = async () => {
        try {
          setLoadingPricingRules(true);
          setError(null);
          const response = await apiService.getPricingRules(
            selectedPricingVehicleTypeId,
            selectedPricingCustomerGroupId
          );
          setPricingRules(response.items);
        } catch (err: any) {
          console.error('Error loading pricing rules:', err);
          setError(err.message || t('solutions.pricingRules.errors.failedToLoad'));
        } finally {
          setLoadingPricingRules(false);
        }
      };
      loadPricingRules();
    }
  }, [
    isPricingRulesModalOpen,
    selectedPricingVehicleTypeId,
    selectedPricingCustomerGroupId,
    t,
  ]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isPricingRulesModalOpen) {
      setSelectedPricingVehicleTypeId('');
      setSelectedPricingCustomerGroupId('');
      setPricingRules([]);
      setEditingPricingRules(new Map());
      setSavingPricingRule(null);
      setError(null);
    }
  }, [isPricingRulesModalOpen]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load categories and vehicle types for admin
        if (user?.userType === 'admin') {
          const [categoriesResponse, vehicleTypesResponse] = await Promise.all([
            apiService.getSolutionCategories(),
            apiService.getVehicleTypes(),
          ]);
          setCategories(categoriesResponse.items);
          setVehicleTypes(vehicleTypesResponse.items);
        }

        // Load solutions first
        const solutionsResponse = await apiService.getSolutions();
        setSolutions(solutionsResponse.items);

        // Load vehicle types for customers (for filtering)
        if (user?.userType === 'customer') {
          const vehicleTypesResponse = await apiService.getVehicleTypes();
          setVehicleTypes(vehicleTypesResponse.items);
          
          // Filter vehicle types to only those that have solutions
          const vehicleTypesWithSolutions = vehicleTypesResponse.items.filter(vType =>
            solutionsResponse.items.some(solution => solution.vehicleTypeId === vType.id)
          );
          
          // Set default vehicle type to "Car" if available and has solutions
          const carType = vehicleTypesWithSolutions.find(
            vt => vt.name.toLowerCase() === 'car'
          );
          if (carType && !selectedVehicleTypeId) {
            setSelectedVehicleTypeId(carType.id);
          } else if (vehicleTypesWithSolutions.length > 0 && !selectedVehicleTypeId) {
            // If "Car" is not available, select the first available vehicle type
            setSelectedVehicleTypeId(vehicleTypesWithSolutions[0].id);
          }
        }

        // For customer, group by levels (like on New File Service page)
        if (user?.userType === 'customer') {
          const sections = buildCustomerSections(
            solutionsResponse.items,
            selectedVehicleTypeId
          );

          setSolutionSections(sections);
        }
      } catch (err: any) {
        console.error('Error loading solutions:', err);
        setError(err.message || t('solutions.errors.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.userType, currentLanguage, selectedVehicleTypeId]);

  // Calculate price when selected solutions or vehicle type changes
  useEffect(() => {
    if (user?.userType === 'customer' && selectedSolutions.length > 0 && selectedVehicleTypeId) {
      const calculatePrice = async () => {
        try {
          setCalculatingPrice(true);
          const response = await apiService.calculatePricing({
            solutions: selectedSolutions,
            vehicleTypeId: selectedVehicleTypeId,
          });
          setTotalPrice(response.creditAmount);
        } catch (err: any) {
          console.error('Error calculating price:', err);
          setTotalPrice(0);
        } finally {
          setCalculatingPrice(false);
        }
      };
      calculatePrice();
    } else {
      setTotalPrice(0);
    }
  }, [selectedSolutions, selectedVehicleTypeId, user?.userType]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleDeleteClick = (solution: Solution) => {
    setDeletingSolution(solution);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingSolution(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingSolution) return;

    try {
      setDeleting(true);
      await apiService.deleteSolution(deletingSolution.externalId);

      // Remove solution from list
      setSolutions(prev =>
        prev.filter(s => s.externalId !== deletingSolution.externalId)
      );

      // If customer, also update sections
      if (user?.userType === 'customer') {
        setSolutionSections(prev =>
          prev
            .map(section => ({
              ...section,
              items: section.items.filter(
                item => item.externalId !== deletingSolution.externalId
              ),
            }))
            .filter(section => section.items.length > 0)
        );
      }

      handleCloseDeleteDialog();
    } catch (err: any) {
      console.error('Error deleting solution:', err);
      setError(err.message || t('solutions.errors.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  const handleEditClick = async (solution: Solution) => {
    setEditingSolution(solution);
    setEditFormData({
      categoryId: solution.categoryId,
      vehicleTypeId: solution.vehicleTypeId,
      name: solution.name,
      alias: solution.alias,
      level: solution.level || 0,
      isFreeWhenStageSelected: solution.isFreeWhenStageSelected || false,
    });
    setIsEditModalOpen(true);
    try {
      const prices = await apiService.getSolutionGroupPrices(solution.externalId);
      setGroupPrices(prices);
    } catch {
      setGroupPrices([]);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingSolution(null);
    setEditFormData({
      categoryId: '',
      vehicleTypeId: '',
      name: '',
      alias: '',
      level: 0,
      isFreeWhenStageSelected: false,
    });
    setGroupPrices([]);
    // setGroupPricesTabIndex(0);
  };

  // const handleGroupPriceChange = (groupId: string, value: number) => {
  //   setGroupPrices(prev =>
  //     prev.map(item =>
  //       item.groupId === groupId ? { ...item, creditAmount: value } : item,
  //     ),
  //   );
  // };

  const handleEditInputChange =
    (field: keyof typeof editFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
        event.target.type === 'number'
          ? parseFloat(event.target.value) || 0
          : event.target.value;
      setEditFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleEditCheckboxChange =
    (field: 'isFreeWhenStageSelected') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEditFormData(prev => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleEditSelectChange =
    (field: 'categoryId' | 'vehicleTypeId') => (event: any) => {
      setEditFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSaveEdit = async () => {
    if (!editingSolution) return;

    // Validate required fields
    if (
      !editFormData.categoryId ||
      !editFormData.vehicleTypeId ||
      !editFormData.name ||
      !editFormData.alias
    ) {
      setError(t('solutions.errors.fillRequiredFields'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updatedSolution = await apiService.updateSolution(
        editingSolution.externalId,
        editFormData
      );

      // Update group prices
      for (const item of groupPrices) {
        await apiService.updateSolutionGroupPrice(
          editingSolution.externalId,
          item.groupId,
          { creditAmount: item.creditAmount },
        );
      }

      // Update solution in list
      setSolutions(prev =>
        prev.map(s =>
          s.externalId === editingSolution.externalId ? updatedSolution : s
        )
      );

      // If customer, also update sections (grouped by levels)
      if (user?.userType === 'customer') {
        const solutionsResponse = await apiService.getSolutions();

        const sections = buildCustomerSections(solutionsResponse.items);

        setSolutionSections(sections);
      }

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating solution:', err);
      setError(err.message || t('solutions.errors.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddClick = () => {
    setAddFormData({
      categoryId: '',
      vehicleTypeId: '',
      name: '',
      alias: '',
      level: 0,
      isFreeWhenStageSelected: false,
    });
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setAddFormData({
      categoryId: '',
      vehicleTypeId: '',
      name: '',
      alias: '',
      level: 0,
      isFreeWhenStageSelected: false,
    });
  };

  const handleAddInputChange =
    (field: keyof typeof addFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
        event.target.type === 'number'
          ? parseFloat(event.target.value) || 0
          : event.target.value;
      setAddFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleAddCheckboxChange =
    (field: 'isFreeWhenStageSelected') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAddFormData(prev => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleAddSelectChange =
    (field: 'categoryId' | 'vehicleTypeId') => (event: any) => {
      setAddFormData(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSaveAdd = async () => {
    // Validate required fields
    if (
      !addFormData.categoryId ||
      !addFormData.vehicleTypeId ||
      !addFormData.name ||
      !addFormData.alias
    ) {
      setError(t('solutions.errors.fillRequiredFields'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const newSolution = await apiService.createSolution(addFormData);

      // Add solution to list
      setSolutions(prev => [...prev, newSolution]);

      // If customer, also update sections (grouped by levels)
      if (user?.userType === 'customer') {
        const solutionsResponse = await apiService.getSolutions();

        const sections = buildCustomerSections(solutionsResponse.items);

        setSolutionSections(sections);
      }

      handleCloseAddModal();
    } catch (err: any) {
      console.error('Error creating solution:', err);
      setError(err.message || t('solutions.errors.failedToCreate'));
    } finally {
      setSaving(false);
    }
  };

  // For admin, show table
  if (user?.userType === 'admin') {
    if (loading) {
      return (
        <Box
          sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert variant="filled" severity="error">{error}</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            {t('solutions.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<IconSettings />}
              onClick={() => setIsPricingRulesModalOpen(true)}
            >
              {t('solutions.pricingRules.title')}
            </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddClick}
          >
            {t('solutions.addSolution')}
          </Button>
          </Box>
        </Box>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">
                        {t('solutions.table.name')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('solutions.table.alias')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('solutions.table.category')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('solutions.table.vehicleType')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('solutions.table.createdAt')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('solutions.table.updatedAt')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="h6">
                        {t('solutions.table.actions')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solutions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('solutions.noSolutionsFound')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    solutions.map(solution => (
                      <TableRow key={solution.externalId} hover>
                        <TableCell>{solution.name}</TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                            }}
                          >
                            {solution.alias}
                          </Typography>
                        </TableCell>
                        <TableCell>{solution.category}</TableCell>
                        <TableCell>{solution.vehicleType}</TableCell>
                        <TableCell>{formatDate(solution.createdAt)}</TableCell>
                        <TableCell>{formatDate(solution.updatedAt)}</TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                          >
                            <Tooltip title={t('solutions.tooltips.edit')}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditClick(solution)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('solutions.tooltips.delete')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(solution)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            {t('solutions.modals.delete.title')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              {t('solutions.modals.delete.confirm', {
                name: deletingSolution?.name || '',
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDialog}
              color="secondary"
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting
                ? t('solutions.modals.delete.deleting')
                : t('solutions.modals.delete.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Solution Modal */}
        <Dialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {t('solutions.modals.edit.title')}
              </Typography>
              <IconButton onClick={handleCloseEditModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            {error && (
              <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}
            >
              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.edit.name')}</InputLabel>
                <OutlinedInput
                  value={editFormData.name}
                  onChange={handleEditInputChange('name')}
                  label={t('solutions.modals.edit.name')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.edit.alias')}</InputLabel>
                <OutlinedInput
                  value={editFormData.alias}
                  onChange={handleEditInputChange('alias')}
                  label={t('solutions.modals.edit.alias')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.edit.category')}</InputLabel>
                <Select
                  label={t('solutions.modals.edit.category')}
                  value={editFormData.categoryId}
                  onChange={handleEditSelectChange('categoryId')}
                >
                  {categories.map(category => (
                    <MenuItem
                      key={category.externalId}
                      value={category.externalId}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>
                  {t('solutions.modals.edit.vehicleType')}
                </InputLabel>
                <Select
                  label={t('solutions.modals.edit.vehicleType')}
                  value={editFormData.vehicleTypeId}
                  onChange={handleEditSelectChange('vehicleTypeId')}
                >
                  {vehicleTypes.map(vehicleType => (
                    <MenuItem key={vehicleType.id} value={vehicleType.id}>
                      {vehicleType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  label="Level"
                  value={editFormData.level}
                  onChange={event =>
                    setEditFormData(prev => ({
                      ...prev,
                      level: Number(event.target.value),
                    }))
                  }
                >
                  {levelOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {editFormData.level === 2 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editFormData.isFreeWhenStageSelected}
                      onChange={handleEditCheckboxChange(
                        'isFreeWhenStageSelected'
                      )}
                    />
                  }
                  label="Is Free When Stage Selected"
                />
              )}

              {/* Group prices section */}
              {/*<Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 2 }}>*/}
              {/*  <Typography variant="subtitle1" sx={{ mb: 2 }}>*/}
              {/*    {t('solutions.modals.edit.groupPrices')}*/}
              {/*  </Typography>*/}
              {/*  /!*{groupPrices.length === 0 ? (*!/*/}
              {/*  /!*  <Typography variant="body2" color="text.secondary">*!/*/}
              {/*  /!*    {t('solutions.modals.edit.noGroupPrices')}*!/*/}
              {/*  /!*  </Typography>*!/*/}
              {/*  /!*) : (*!/*/}
              {/*  /!*  <>*!/*/}
              {/*  /!*    <Tabs*!/*/}
              {/*  /!*      value={groupPricesTabIndex}*!/*/}
              {/*  /!*      onChange={(_, v: number) => setGroupPricesTabIndex(v)}*!/*/}
              {/*  /!*      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}*!/*/}
              {/*  /!*    >*!/*/}
              {/*  /!*      {groupPrices.map(item => (*!/*/}
              {/*  /!*        <Tab key={item.groupId} label={item.groupName} />*!/*/}
              {/*  /!*      ))}*!/*/}
              {/*  /!*    </Tabs>*!/*/}
              {/*  /!*    {groupPrices.map((item, index) => (*!/*/}
              {/*  /!*      <div*!/*/}
              {/*  /!*        key={item.groupId}*!/*/}
              {/*  /!*        role="tabpanel"*!/*/}
              {/*  /!*        hidden={groupPricesTabIndex !== index}*!/*/}
              {/*  /!*        id={`solution-group-price-tabpanel-${index}`}*!/*/}
              {/*  /!*        aria-labelledby={`solution-group-price-tab-${index}`}*!/*/}
              {/*  /!*      >*!/*/}
              {/*  /!*        {groupPricesTabIndex === index && (*!/*/}
              {/*  /!*          <Box sx={{ pt: 1 }}>*!/*/}
              {/*  /!*            <FormControl fullWidth>*!/*/}
              {/*  /!*              <InputLabel>*!/*/}
              {/*  /!*                {t('solutions.modals.edit.creditAmount')}*!/*/}
              {/*  /!*              </InputLabel>*!/*/}
              {/*  /!*              <OutlinedInput*!/*/}
              {/*  /!*                type="number"*!/*/}
              {/*  /!*                value={item.creditAmount}*!/*/}
              {/*  /!*                onChange={e =>*!/*/}
              {/*  /!*                  handleGroupPriceChange(*!/*/}
              {/*  /!*                    item.groupId,*!/*/}
              {/*  /!*                    parseFloat(e.target.value) || 0,*!/*/}
              {/*  /!*                  )*!/*/}
              {/*  /!*                }*!/*/}
              {/*  /!*                label={t(*!/*/}
              {/*  /!*                  'solutions.modals.edit.creditAmount',*!/*/}
              {/*  /!*                )}*!/*/}
              {/*  /!*              />*!/*/}
              {/*  /!*            </FormControl>*!/*/}
              {/*  /!*          </Box>*!/*/}
              {/*  /!*        )}*!/*/}
              {/*  /!*      </div>*!/*/}
              {/*  /!*    ))}*!/*/}
              {/*  /!*  </>*!/*/}
              {/*  /!*)}*!/*/}
              {/*</Box>*/}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseEditModal}
              color="secondary"
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveEdit}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving
                ? t('solutions.modals.edit.saving')
                : t('solutions.modals.edit.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Solution Modal */}
        <Dialog
          open={isAddModalOpen}
          onClose={handleCloseAddModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {t('solutions.modals.add.title')}
              </Typography>
              <IconButton onClick={handleCloseAddModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}
            >
              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.add.name')}</InputLabel>
                <OutlinedInput
                  value={addFormData.name}
                  onChange={handleAddInputChange('name')}
                  label={t('solutions.modals.add.name')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.add.alias')}</InputLabel>
                <OutlinedInput
                  value={addFormData.alias}
                  onChange={handleAddInputChange('alias')}
                  label={t('solutions.modals.add.alias')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.add.category')}</InputLabel>
                <Select
                  label={t('solutions.modals.add.category')}
                  value={addFormData.categoryId}
                  onChange={handleAddSelectChange('categoryId')}
                >
                  {categories.map(category => (
                    <MenuItem
                      key={category.externalId}
                      value={category.externalId}
                    >
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('solutions.modals.add.vehicleType')}</InputLabel>
                <Select
                  label={t('solutions.modals.add.vehicleType')}
                  value={addFormData.vehicleTypeId}
                  onChange={handleAddSelectChange('vehicleTypeId')}
                >
                  {vehicleTypes.map(vehicleType => (
                    <MenuItem key={vehicleType.id} value={vehicleType.id}>
                      {vehicleType.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Level</InputLabel>
                <Select
                  label="Level"
                  value={addFormData.level}
                  onChange={event =>
                    setAddFormData(prev => ({
                      ...prev,
                      level: Number(event.target.value),
                    }))
                  }
                >
                  {levelOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {addFormData.level === 2 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addFormData.isFreeWhenStageSelected}
                      onChange={handleAddCheckboxChange(
                        'isFreeWhenStageSelected'
                      )}
                    />
                  }
                  label="Is Free When Stage Selected"
                />
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={handleCloseAddModal}
              color="secondary"
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveAdd}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving
                ? t('solutions.modals.add.creating')
                : t('solutions.modals.add.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Pricing Rules Modal */}
        <Dialog
          open={isPricingRulesModalOpen}
          onClose={() => setIsPricingRulesModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {t('solutions.pricingRules.title')}
              </Typography>
              <IconButton onClick={() => setIsPricingRulesModalOpen(false)} size="small">
                <Close />
              </IconButton>
      </Box>
          </DialogTitle>

          <DialogContent>
            {error && (
              <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('solutions.pricingRules.vehicleType')}</InputLabel>
                  <Select
                    label={t('solutions.pricingRules.vehicleType')}
                    value={selectedPricingVehicleTypeId}
                    onChange={(e) => {
                      setSelectedPricingVehicleTypeId(e.target.value);
                      setPricingRules([]);
                    }}
                  >
                    {vehicleTypes.map((vehicleType) => (
                      <MenuItem key={vehicleType.id} value={vehicleType.id}>
                        {vehicleType.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('solutions.pricingRules.customerGroup')}</InputLabel>
                  <Select
                    label={t('solutions.pricingRules.customerGroup')}
                    value={selectedPricingCustomerGroupId}
                    onChange={(e) => {
                      setSelectedPricingCustomerGroupId(e.target.value);
                      setPricingRules([]);
                    }}
                  >
                    {customerGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {!selectedPricingVehicleTypeId || !selectedPricingCustomerGroupId ? (
                <Alert severity="info">
                  {t('solutions.pricingRules.selectBoth')}
                </Alert>
              ) : loadingPricingRules ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : pricingRules.length === 0 ? (
                <Alert severity="info">
                  {t('solutions.pricingRules.noRules')}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('solutions.pricingRules.solutionName')}</TableCell>
                        <TableCell>{t('solutions.pricingRules.creditAmount')}</TableCell>
                        <TableCell align="right">{t('solutions.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pricingRules.map((rule) => {
                        const solution = solutions.find((s) => s.alias === rule.alias);
                        const displayName = solution?.name || rule.alias;
                        const editedAmount = editingPricingRules.get(rule.externalId);
                        const currentAmount = editedAmount !== undefined ? editedAmount : rule.creditAmount;
                        const isSaving = savingPricingRule === rule.externalId;
                        const hasChanges = editedAmount !== undefined && editedAmount !== rule.creditAmount;

                        return (
                          <TableRow key={rule.externalId}>
                            <TableCell>{displayName}</TableCell>
                            <TableCell>
                              <OutlinedInput
                                type="number"
                                value={currentAmount}
                                onChange={(e) => {
                                  const newValue = parseFloat(e.target.value) || 0;
                                  setEditingPricingRules((prev) => {
                                    const newMap = new Map(prev);
                                    newMap.set(rule.externalId, newValue);
                                    return newMap;
                                  });
                                }}
                                size="small"
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="contained"
                                size="small"
                                onClick={async () => {
                                  const amountToSave = editingPricingRules.get(rule.externalId);
                                  if (amountToSave === undefined || amountToSave === rule.creditAmount) {
                                    return;
                                  }

                                  try {
                                    setSavingPricingRule(rule.externalId);
                                    setError(null);
                                    await apiService.updatePricingRule(rule.externalId, {
                                      creditAmount: amountToSave,
                                    });
                                    setPricingRules((prev) =>
                                      prev.map((r) =>
                                        r.externalId === rule.externalId
                                          ? { ...r, creditAmount: amountToSave }
                                          : r
                                      )
                                    );
                                    setEditingPricingRules((prev) => {
                                      const newMap = new Map(prev);
                                      newMap.delete(rule.externalId);
                                      return newMap;
                                    });
                                  } catch (err: any) {
                                    console.error('Error updating pricing rule:', err);
                                    setError(err.message || t('solutions.pricingRules.errors.failedToUpdate'));
                                  } finally {
                                    setSavingPricingRule(null);
                                  }
                                }}
                                disabled={isSaving || !hasChanges}
                              >
                                {isSaving
                                  ? t('solutions.pricingRules.saving')
                                  : t('solutions.pricingRules.save')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setIsPricingRulesModalOpen(false)}
              color="secondary"
            >
              {t('common.close')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  const sectionLabelStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    px: 2,
    py: 0.75,
    borderRadius: 1,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: 'uppercase' as const,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.12)
        : alpha(theme.palette.primary.main, 0.15),
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.dark,
  };

  const tileBackground =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.85)
      : theme.palette.background.paper;

  const tileBorderColor = alpha(
    theme.palette.primary.main,
    theme.palette.mode === 'dark' ? 0.25 : 0.12
  );

  const tileIconBackground = alpha(theme.palette.primary.main, 0.14);
  const tileShadow =
    theme.palette.mode === 'dark'
      ? '0 8px 22px rgba(0,0,0,0.35)'
      : '0 8px 18px rgba(15,23,42,0.08)';
  const tileHoverShadow =
    theme.palette.mode === 'dark'
      ? '0 12px 30px rgba(0,0,0,0.45)'
      : '0 12px 30px rgba(15,23,42,0.12)';

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedVehicleTypeId(newValue);
    setSelectedSolutions([]);
    setTotalPrice(0);
  };

  // Filter vehicle types to show only those that have solutions
  const vehicleTypesWithSolutions = vehicleTypes.filter(vType => 
    solutions.some(solution => solution.vehicleTypeId === vType.id)
  );

  return (
    <Box sx={{ p: 3, pb: 6 }}>
      {/* Vehicle Type Tabs */}
      {vehicleTypesWithSolutions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={selectedVehicleTypeId}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
              },
            }}
          >
            {vehicleTypesWithSolutions.map(vType => (
              <Tab
                key={vType.id}
                label={vType.name}
                value={vType.id}
                sx={{
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  minWidth: 120,
                }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {solutionSections.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" color="text.secondary">
            {t('solutions.noSolutionsAvailable')}
          </Typography>
        </Box>
      ) : (
        <>
          {selectedSolutions.length > 0 && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                border: '1px solid',
                borderColor: theme.palette.primary.main,
              }}
            >
              <Typography
                variant="body1"
                color="primary"
                sx={{ fontWeight: 600 }}
              >
                {t('newFileService.solutions.selected')}{' '}
                {selectedSolutions.length}{' '}
                {selectedSolutions.length === 1
                  ? t('newFileService.solutions.solution')
                  : t('newFileService.solutions.solutions')}
                {calculatingPrice ? (
                  <>
                    {' '}· {t('newFileService.solutions.totalAmount')}{' '}
                    <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />
                  </>
                ) : (
                  selectedSolutions.length > 0 && (
                    <>
                      {' '}· {t('newFileService.solutions.totalAmount')} {totalPrice}{' '}
                      {t('newFileService.solutions.credits')}
                    </>
                  )
                )}
              </Typography>
            </Box>
          )}
          {solutionSections.map(section => (
            <Box key={section.title} sx={{ mb: 5 }}>
              <Typography variant="caption" sx={sectionLabelStyles}>
                {section.title}
              </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {section.items.map(item => {
                const isSelected = selectedSolutions.includes(item.externalId);
                return (
                  <Grid key={item.externalId} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper
                      elevation={0}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedSolutions(prev =>
                            prev.filter(id => id !== item.externalId)
                          );
                        } else {
                          setSelectedSolutions(prev => [...prev, item.externalId]);
                        }
                      }}
                      sx={{
                        p: 2.5,
                        height: '100%',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isSelected
                          ? theme.palette.primary.main
                          : tileBorderColor,
                        backgroundColor: isSelected
                          ? alpha(theme.palette.primary.main, 0.08)
                          : tileBackground,
                        transition: 'all 0.2s ease',
                        boxShadow: tileShadow,
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          transform: 'translateY(-4px)',
                          boxShadow: tileHoverShadow,
                        },
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            backgroundColor: isSelected
                              ? theme.palette.primary.main
                              : tileIconBackground,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: isSelected
                              ? theme.palette.primary.contrastText
                              : theme.palette.primary.main,
                          }}
                        >
                          <IconSettings size={24} />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            flex: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{ lineHeight: 1.3 }}
                          >
                            {item.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: alpha(theme.palette.text.secondary, 0.8),
                              textTransform: 'uppercase',
                              letterSpacing: 0.6,
                            }}
                          >
                            {item.alias}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
        </>
      )}
    </Box>
  );
};

export default SolutionsPage;
